import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import DetailCard from '../../components/detailCard/DetailCard';
import CustomTable from '../../components/customtable/CustomTable';
import CustomDatePicker from '../../components/filter/CustomDatePicker';
import toastCenter from '../../utils/toastCenter';
import APPCONSTANTS from '../../constants/appConstants';
import Loader from '../../components/loader/Loader';
import {
  skDetailsListSelector,
  skDetailsLoadingSelector,
  skDetailsTotalSelector
} from '../../store/report/selectors';
import ERRORS from '../../constants/error';
import { clearSKDetailsView, fetchSKDetailsListRequest, fetchSKDetailsViewRequest } from '../../store/report/actions';
import { ISKDetailsListRequestPayload, ISKDetailsRow } from '../../store/report/types';
import SKDetailsViewModal from './SKDetailsViewModal';
import styles from './SKDetails.module.scss';

export interface ISKDetailsPeriod {
  startDate?: string;
  endDate?: string;
}

const buildPeriod = (fromDate: Date | null, toDate: Date | null): ISKDetailsPeriod => ({
  ...(fromDate && { startDate: moment(fromDate).format(APPCONSTANTS.YEAR_MONTH_DATE_FORMAT) }),
  ...(toDate && { endDate: moment(toDate).format(APPCONSTANTS.YEAR_MONTH_DATE_FORMAT) })
});

const buildSKDetailsPayload = (
  period: ISKDetailsPeriod,
  searchTerm: string,
  pagination?: { skip: number; limit: number }
): ISKDetailsListRequestPayload => ({
  ...(searchTerm && { searchTerm }),
  ...period,
  skip: pagination?.skip,
  limit: pagination?.limit
});

const SKDetails = (): React.ReactElement => {
  const dispatch = useDispatch();
  const skList = useSelector(skDetailsListSelector);
  const isListLoading = useSelector(skDetailsLoadingSelector);
  const totalCount = useSelector(skDetailsTotalSelector);
  const [page, setPage] = useState(APPCONSTANTS.NUMBER.ONE);
  const [rowsPerPage, setRowsPerPage] = useState(APPCONSTANTS.ROWS_PER_PAGE_OF_TABLE);
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [appliedPeriod, setAppliedPeriod] = useState<ISKDetailsPeriod>({});
  const [showViewModal, setShowViewModal] = useState(false);
  const fromDatePickerRef = useRef<DatePicker>(null);
  const toDatePickerRef = useRef<DatePicker>(null);

  const fetchPaginatedSKDetails = (
    period: ISKDetailsPeriod,
    search: string,
    nextPage: number,
    nextRowsPerPage: number
  ) => {
    const payload = buildSKDetailsPayload(period, search, {
      skip: (nextPage - 1) * nextRowsPerPage,
      limit: nextRowsPerPage
    });

    dispatch(
      fetchSKDetailsListRequest({
        payload,
        failureCb: (error) => {
          if (error.message === ERRORS.NETWORK_ERROR.message) {
            toastCenter.error(APPCONSTANTS.NETWORK_ERROR, APPCONSTANTS.CONNECTION_LOST);
          } else {
            toastCenter.error(APPCONSTANTS.OOPS, APPCONSTANTS.SK_DETAILS_FETCH_ERROR);
          }
        }
      })
    );
  };

  useEffect(() => {
    fetchPaginatedSKDetails({}, '', APPCONSTANTS.NUMBER.ONE, rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApplyPeriod = () => {
    const period = buildPeriod(fromDate, toDate);
    setAppliedPeriod(period);
    setPage(APPCONSTANTS.NUMBER.ONE);
    fetchPaginatedSKDetails(period, searchTerm, APPCONSTANTS.NUMBER.ONE, rowsPerPage);
  };

  const handleClearPeriod = () => {
    setFromDate(null);
    setToDate(null);
    setAppliedPeriod({});
    setPage(APPCONSTANTS.NUMBER.ONE);
    fetchPaginatedSKDetails({}, searchTerm, APPCONSTANTS.NUMBER.ONE, rowsPerPage);
  };

  const handleRowView = (rowData: ISKDetailsRow) => {
    setShowViewModal(true);
    dispatch(
      fetchSKDetailsViewRequest({
        payload: { skUserId: rowData.userId, ...appliedPeriod },
        failureCb: (error) => {
          setShowViewModal(false);
          if (error.message === ERRORS.NETWORK_ERROR.message) {
            toastCenter.error(APPCONSTANTS.NETWORK_ERROR, APPCONSTANTS.CONNECTION_LOST);
          } else {
            toastCenter.error(APPCONSTANTS.OOPS, APPCONSTANTS.SK_DETAILS_VIEW_FETCH_ERROR);
          }
        }
      })
    );
  };

  const handleViewModalClose = () => {
    setShowViewModal(false);
    dispatch(clearSKDetailsView());
  };

  return (
    <div className='row'>
      <div className='col'>
        <DetailCard
          header='SK Coverage'
          isSearch={true}
          onSearch={(search: string) => {
            setSearchTerm(search);
            setPage(APPCONSTANTS.NUMBER.ONE);
            fetchPaginatedSKDetails(appliedPeriod, search, APPCONSTANTS.NUMBER.ONE, rowsPerPage);
          }}
        >
          <div>
            <div className={styles.periodBar}>
              <div className={styles.periodField}>
                <span className={styles.periodLabel}>Start Date</span>
                <CustomDatePicker
                  pickerRef={fromDatePickerRef}
                  dayReducer={0}
                  selectedDate={fromDate}
                  maxDate={toDate || undefined}
                  onDateSelect={(date: Date) => setFromDate(date)}
                />
              </div>
              <div className={styles.periodField}>
                <span className={styles.periodLabel}>End Date</span>
                <CustomDatePicker
                  pickerRef={toDatePickerRef}
                  dayReducer={0}
                  selectedDate={toDate}
                  minDate={fromDate || undefined}
                  onDateSelect={(date: Date) => setToDate(date)}
                />
              </div>
              <button type='button' className='btn primary-btn' onClick={handleApplyPeriod} disabled={isListLoading}>
                Apply
              </button>
              <button
                type='button'
                className='btn secondary-btn'
                onClick={handleClearPeriod}
                disabled={isListLoading || (!fromDate && !toDate && !appliedPeriod.startDate && !appliedPeriod.endDate)}
              >
                Clear
              </button>
              {(appliedPeriod.startDate || appliedPeriod.endDate) && (
                <span className={styles.periodHint}>
                  {`Counts limited to ${appliedPeriod.startDate || 'beginning'} → ${
                    appliedPeriod.endDate || 'today'
                  }`}
                </span>
              )}
            </div>
            {isListLoading ? (
              <Loader />
            ) : (
              <CustomTable
                rowData={skList}
                page={page}
                rowsPerPage={rowsPerPage}
                count={totalCount}
                handlePageChange={(pageNo: number, nextRowsPerPage?: number) => {
                  const updatedRowsPerPage = nextRowsPerPage || rowsPerPage;
                  setPage(pageNo);
                  setRowsPerPage(updatedRowsPerPage);
                  fetchPaginatedSKDetails(appliedPeriod, searchTerm, pageNo, updatedRowsPerPage);
                }}
                columnsDef={[
                  { id: 1, name: 'username', label: 'Username', width: '130px' },
                  { id: 2, name: 'name', label: 'Name' },
                  { id: 3, name: 'phoneNumber', label: 'Phone Number', width: '140px' },
                  { id: 4, name: 'unionNames', label: 'Union' },
                  { id: 5, name: 'upazila', label: 'Upazila' },
                  { id: 6, name: 'activeSsCount', label: 'Active SS' },
                  { id: 7, name: 'subVillageCount', label: 'Villages' },
                  { id: 8, name: 'householdCount', label: 'Households' },
                  { id: 9, name: 'memberCount', label: 'Members' },
                  { id: 10, name: 'totalServiceCount', label: 'Services' },
                  {
                    id: 11,
                    name: 'isActive',
                    label: 'Status',
                    cellFormatter: (data: ISKDetailsRow) => (data.isActive ? 'Active' : 'Inactive')
                  }
                ]}
                isDelete={false}
                isEdit={false}
                isView={true}
                viewToolTip='View SK details'
                onRowView={handleRowView}
                isDownload={false}
              />
            )}
          </div>
        </DetailCard>
      </div>
      <SKDetailsViewModal show={showViewModal} onClose={handleViewModalClose} period={appliedPeriod} />
    </div>
  );
};

export default SKDetails;
export { buildSKDetailsPayload, buildPeriod };
